import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SecurityTiles from './SecurityTiles';
import { formSchema, type FormData } from '../types/form';
import type { KbaQuestion } from '../types/api';
import {
  formatPhoneNumber,
  validateEmail,
  validatePhone,
} from '../utils/validation';
import { FormController, type EchelonProgressStage } from '../controllers/FormController';
import { storageController } from '../controllers/StorageController';
import {
  FormRouteManager,
  FormRouteEnum,
  getFlowType,
  shouldRedirectToStart,
  type FlowType,
  type FormRoute,
  useFormRouting,
} from '../routes/FormRoutes';
import { marketingParams } from '../utils/marketingParams';
import StartForm from './forms/StartForm';
import ApplicationReviewForm from './forms/ApplicationReviewForm';
import LoanPurposeForm from './forms/LoanPurposeForm';
import LoanAmountForm from './forms/LoanAmountForm';
import PropertyStatusForm from './forms/PropertyStatusForm';
import EmploymentStatusForm from './forms/EmploymentStatusForm';
import EmploymentFrequencyForm from './forms/EmploymentFrequencyForm';
import AnnualIncomeForm from './forms/AnnualIncomeForm';
import EducationForm from './forms/EducationForm';
import EmailForm from './forms/EmailForm';
import BirthDateForm from './forms/BirthDateForm';
import ContactInfoForm from './forms/ContactInfoForm';
import { useCountdown } from '../hooks/useCountdown';
import { useAttemptLock } from '../hooks/useAttemptLock';
import PhoneVerificationForm from './forms/PhoneVerificationForm';
import SSNVerificationForm from './forms/SSNVerificationForm';
import KbaPersonalInfoForm from './forms/KbaPersonalInfoForm';
import KbaSsnForm from './forms/KbaSsnForm';
import KbaQuestionsForm from './forms/KbaQuestionsForm';
import ManualVerificationForm from './forms/ManualVerificationForm';
import SuccessStep from './forms/SuccessStepModular';
import EmailVerificationModal from './EmailVerificationModal';
import ProgressBar from './ProgressBar';
import FormHeader from './FormHeader';
import LoadingOverlay from './LoadingOverlay';
import EchelonProgressOverlay from './EchelonProgressOverlay';
import TrustpilotWidget from './TrustpilotWidget';
import ApplicationBlocked from './forms/ApplicationBlocked';
import UnsuccessfulApplication from './forms/UnsuccessfulApplication';
import DeveloperTools from './DeveloperTools';
import { campaignVariantService } from '../services/campaignVariants';
import { formDefaultsService } from '../services/formDefaults';
import { clientConfig } from '../config/clientConfig';
import { trackFormStep } from '../utils/formTrack';
import { usePartnerDefaults, readPartnerIdFromUrl, resetStorageForNewPartnerLanding } from '../hooks/usePartnerDefaults';
import { ReviewEditController } from '../routes/reviewEditController';
import { hasPartnerReviewData } from '../utils/partnerReview';
import { useLeadPreload, isAcceptedApplicationStatus } from '../hooks/useLeadPreload';
import { createLeadsService } from '../services/leadsFactory';
import {
  MAX_ATTEMPTS_PER_METHOD,
  isSmsExhausted,
  isProspectExhausted,
  isKbaExhausted,
  areAllMethodsExhausted,
} from '../utils/verificationAttempts';
import { shouldFallback } from '../utils/apiFallback';
import { classifySessionOutcome } from '../utils/sessionOutcome';
import { resolveOffersRedirect } from '../utils/offersRedirect';
import { resolveTerminalAccess, type TerminalAccess } from '../utils/terminalAccess';
import type { AlternativeMethodCallbacks } from '../types/verification';

const SKIP_FREQUENCY_STATUSES = ['not_employed', 'retired', 'other'];
const AUTO_ECHELON_DEBT_THRESHOLD = 1500000;
/** How long a connect 429 or an exhausted method waits: the BFF's ATTEMPT_LOCK_SECONDS. */
const CONNECT_COOLDOWN_MS = 5 * 60 * 1000;

type ManualVerificationReason = 'sms_failure' | 'create_failure';

const ROUTES_WITHOUT_BACK = new Set<string>([
  FormRouteEnum.START,
  FormRouteEnum.VERIFY_SSN,
  FormRouteEnum.SUCCESS,
  FormRouteEnum.UNSUCCESSFUL,
  FormRouteEnum.BLOCK,
]);

export default function MicroForm() {
  const { t } = useTranslation('common');
  const { currentPath, navigateToRoute } = useFormRouting();

  // Must run before formController/formData below read from storage, so a
  // fresh partner_id landing never inherits a stale blocked or in-progress
  // session left over from a previous visit on this device.
  useState(() => {
    resetStorageForNewPartnerLanding(readPartnerIdFromUrl());
    return null;
  });

  const mockDelay = 5000;
  const delay = async () => {
    await new Promise((resolve) => setTimeout(resolve, mockDelay));
  };

  // Check for BPO agent flag in URL
  const [isBpoAgent] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('bpo') === 'true';
  });

  const [flow] = useState<FlowType>(() => getFlowType());

  const [formController] = useState(() => {
    const controller = new FormController();
    // Set FormController reference in StorageController
    storageController.setFormController(controller);
    return controller;
  });

  const [partnerService] = useState(() => createLeadsService());
  const { partnerDefaults, isLoading: isPartnerLoading } = usePartnerDefaults(partnerService);
  const { leadDetails, isLoading: isLeadPreloadLoading } = useLeadPreload(partnerService);
  const reviewEditController = useRef(new ReviewEditController()).current;

  const [routeManager] = useState(() => new FormRouteManager(flow));
  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = storageController.getFormData();
    const variant = campaignVariantService.getCurrentVariant();
    const defaults = formDefaultsService.getDefaults();
    
    const baseFormData: FormData = {
      email: savedData?.email || '',
      phone: savedData?.phone || '',
      birthDate: savedData?.birthDate || '',
      smsConsent: savedData?.smsConsent ?? true,
      promoSmsConsent: savedData?.promoSmsConsent || false,
      smsCode: savedData?.smsCode || '',
      ssnLast4: savedData?.ssnLast4 || '',
      loanAmount: savedData?.loanAmount,
      loanPurpose: savedData?.loanPurpose,
      propertyStatus: savedData?.propertyStatus,
      employmentFrequency: savedData?.employmentFrequency,
      employmentStatus: savedData?.employmentStatus,
      annualIncome: savedData?.annualIncome,
      educationLevel: savedData?.educationLevel,
      offerCode: savedData?.offerCode || variant.offerCode,
      firstName: savedData?.firstName || variant.firstName
    };
    
    // Apply form defaults
    return formDefaultsService.applyToFormData(baseFormData) as FormData;
  });

  const [validationErrors, setValidationErrors] = useState<{
    email: string | null;
    phone: string | null;
  }>({
    email: null,
    phone: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [echelonStage, setEchelonStage] = useState<EchelonProgressStage | null>(null);
  const [manualVerificationReason, setManualVerificationReason] = useState<ManualVerificationReason | null>(null);
  const [isUnqualified, setIsUnqualified] = useState(false);
  const [contactResult, setContactResult] = useState<'accepted' | 'rejected' | null>(null);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [smsAttempts, setSmsAttempts] = useState(0);
  // After the SMS lockout the BFF refuses the old code; only a new send restarts the attempts.
  const [smsNeedsNewCode, setSmsNeedsNewCode] = useState(false);
  const [connectCooldownUntil, setConnectCooldownUntil] = useState<number | null>(() =>
    storageController.getConnectCooldownUntil()
  );
  const connectCooldownSecondsLeft = useCountdown(connectCooldownUntil);
  const [kbaQuestions, setKbaQuestions] = useState<KbaQuestion[]>([]);
  const [kbaAttemptCount, setKbaAttemptCount] = useState(1);
  const prospectLock = useAttemptLock(CONNECT_COOLDOWN_MS);
  const kbaLock = useAttemptLock(CONNECT_COOLDOWN_MS, () => {
    // The BFF refuses the old questions after the lockout; new ones come from a new KBA connect.
    setKbaAttemptCount(1);
    setKbaQuestions([]);
  });
  const prospectAttempts = prospectLock.attempts;
  const kbaAttempts = kbaLock.attempts;
  const MAX_KBA_ATTEMPTS = 10;
  const [contactCenterAvailability, setContactCenterAvailability] = useState<
    'none' | 'low' | 'high' | null
  >(null);
  const [shouldSendCall, setShouldSendCall] = useState(false);
  const isTerminalRoute = currentPath === FormRouteEnum.SUCCESS || currentPath === FormRouteEnum.BLOCK;
  const [terminalAccess, setTerminalAccess] = useState<TerminalAccess>('checking');

  const showPartnerReview = Boolean(partnerDefaults) && !isPartnerLoading && hasPartnerReviewData(formData);

  const currentStep = routeManager.getCurrentStep(currentPath);
  const verifyPhoneStep = routeManager.getCurrentStep(FormRouteEnum.VERIFY_PHONE);
  const progressStep = currentStep === -1 ? verifyPhoneStep : currentStep;
  const totalSteps = flow === 'N1' && !isUnqualified
    ? routeManager.getQualifiedFlowTotalSteps()
    : routeManager.getTotalSteps();
  // The partner review page already reflects a partially-completed
  // application (most fields are prefilled), so show ~50% progress instead
  // of the 0% a brand-new START step would otherwise imply.
  const displayProgressStep = currentPath === FormRouteEnum.START && showPartnerReview
    ? Math.round(totalSteps / 2)
    : progressStep;

  useEffect(() => {
    trackFormStep(currentPath as FormRoute);
  }, [currentPath]);
  // Initialize marketing params on first load
  useEffect(() => {
    try {
      // Initialize campaign variants first
      campaignVariantService.initializeFromUrl();
      formDefaultsService.initializeFromUrl();
      marketingParams.initializeFromUrl();
    } catch (err) {
      console.error('Failed to initialize marketing params:', err);
    }
  }, []);

  useEffect(() => {
    storageController.setFormData(formData);
  }, [formData]);

  // With a server-managed (BFF) session the render state lives behind the
  // session cookie, so recover it once on mount; legacy services return null.
  useEffect(() => {
    formController.restoreServerSession().catch(() => void 0);
  }, [formController]);

  useEffect(() => {
    if (!partnerDefaults) return;
    formDefaultsService.injectExternalDefaults(partnerDefaults);
    setFormData(prev => formDefaultsService.applyToFormData(prev) as FormData);
  }, [partnerDefaults]);

  // An exhausted SMS is locked out for CONNECT_COOLDOWN_MS, so with every method
  // exhausted the applicant waits out that countdown on the contact step.
  useEffect(() => {
    const exhausted = areAllMethodsExhausted({ smsAttempts, prospectAttempts, kbaAttempts });
    const isTerminalRoute = (
      currentPath === FormRouteEnum.UNSUCCESSFUL ||
      currentPath === FormRouteEnum.SUCCESS ||
      currentPath === FormRouteEnum.BLOCK ||
      currentPath === FormRouteEnum.CONTACT_INFO
    );
    if (exhausted && !isTerminalRoute) {
      navigateToRoute(FormRouteEnum.CONTACT_INFO);
    }
  }, [smsAttempts, prospectAttempts, kbaAttempts, currentPath, navigateToRoute]);

  useEffect(() => {
    if (connectCooldownUntil !== null && connectCooldownSecondsLeft === 0) {
      storageController.clearConnectCooldownUntil();
      setConnectCooldownUntil(null);
      if (isSmsExhausted(smsAttempts)) {
        setSmsAttempts(0);
        setSmsNeedsNewCode(true);
      }
    }
  }, [connectCooldownUntil, connectCooldownSecondsLeft, smsAttempts]);

  const startConnectCooldown = () => {
    const until = Date.now() + CONNECT_COOLDOWN_MS;
    storageController.setConnectCooldownUntil(until);
    setConnectCooldownUntil(until);
  };

  /** SMS just ran out: lock it out for the cooldown, keeping a countdown that is already running. */
  const lockOutSms = () => {
    setSmsAttempts(MAX_ATTEMPTS_PER_METHOD);
    if (connectCooldownUntil === null) startConnectCooldown();
  };

  const countSmsFailure = () => {
    if (isSmsExhausted(smsAttempts + 1)) lockOutSms();
    else setSmsAttempts(smsAttempts + 1);
  };

  useEffect(() => {
    if (currentPath === FormRouteEnum.KBA_PERSONAL_INFO && !formData.email) {
      navigateToRoute(FormRouteEnum.START);
    }
    if (currentPath === FormRouteEnum.KBA_SSN && !formData.firstName) {
      navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
    }
    if (currentPath === FormRouteEnum.KBA_QUESTIONS && kbaQuestions.length === 0) {
      navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
    }
    if (currentPath === FormRouteEnum.MANUAL_VERIFY && !formData.email) {
      navigateToRoute(FormRouteEnum.START);
    }
  }, [currentPath, formData.email, formData.firstName, kbaQuestions.length, navigateToRoute]);

  useEffect(() => {
    if (
      storageController.isApplicationBlocked() &&
      !routeManager.isBlockedRoute(currentPath as FormRoute)
    ) {
      navigateToRoute(FormRouteEnum.BLOCK);
    }
  }, [currentPath, navigateToRoute]);

  useEffect(() => {
    if (
      shouldRedirectToStart({
        flow,
        currentPath,
        hasStartedForm: formData.email !== '',
      })
    ) {
      navigateToRoute(FormRouteEnum.START);
    }
  }, [currentPath, flow, formData.email, navigateToRoute]);

  // The stage read on mount is stale once an applicant finishes the form here, so read it again on arrival.
  useEffect(() => {
    if (!isTerminalRoute) {
      setTerminalAccess('checking');
      return;
    }
    let cancelled = false;
    const resolveSession = async () => {
      const session = await formController.restoreServerSession();
      const isLegacyApplicant = session?.stage === 'anonymous'
        && storageController.isApplicationBlocked()
        && storageController.isApplicationSuccessful();
      return isLegacyApplicant ? (await formController.claimLegacySession()) ?? session : session;
    };
    resolveSession().then(session => {
      if (cancelled) return;
      setTerminalAccess(resolveTerminalAccess({
        serverStage: session?.stage ?? null,
        hasRecentRejection: storageController.isApplicationBlocked() && !storageController.isApplicationSuccessful(),
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [isTerminalRoute, formController]);

  useEffect(() => {
    if (terminalAccess === 'start-over') {
      storageController.clearAll();
      navigateToRoute(FormRouteEnum.START);
    } else if (terminalAccess === 'local-only' && currentPath === FormRouteEnum.SUCCESS) {
      navigateToRoute(FormRouteEnum.BLOCK);
    }
  }, [terminalAccess, currentPath, navigateToRoute]);

  // Check contact center availability and track success page when reaching success step
  useEffect(() => {
    const checkAvailability = async () => {
      if (currentPath === FormRouteEnum.SUCCESS && terminalAccess === 'full') {
        if (isBpoAgent) {
          setContactCenterAvailability('none');
          setShouldSendCall(false);
          return;
        }

        try {
          const result = await formController.checkContactCenterAvailability();
          if (result.success) {
            setContactCenterAvailability(result.availability);
            setShouldSendCall(result.shouldSendCall);
          } else {
            setContactCenterAvailability('none');
            setShouldSendCall(false);
          }
        } catch (err) {
          console.error('Failed to check contact center availability:', err);
          setContactCenterAvailability('none');
          setShouldSendCall(false);
        }
      }
    };

    checkAvailability();
  }, [currentPath, isBpoAgent, terminalAccess]);

  // Deep-link straight to the success page when returning via a lead unique id
  // (e.g. ?useruniqueId=<guid>) whose application has already been accepted.
  // This only fires ONCE per page load: after the initial redirect, navigating
  // back/away (e.g. to /hold) or reloading while already blocked must behave
  // exactly like the regular flow, so it defers to the isApplicationBlocked
  // guard instead of forcing the user back to the success page every time.
  const [hasHandledLeadRedirect, setHasHandledLeadRedirect] = useState(false);

  useEffect(() => {
    if (!leadDetails) return;
    if (hasHandledLeadRedirect) return;
    setHasHandledLeadRedirect(true);

    if (!isAcceptedApplicationStatus(leadDetails.applicationStatus)) return;
    if (routeManager.isBlockedRoute(currentPath as FormRoute)) return;

    formController.hydrateFromLeadDetails(leadDetails);

    storageController.setApplicationData({
      timestamp: Date.now(),
      email: leadDetails.email ?? formData.email,
      formData: {},
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationSuccess();
    navigateToRoute(FormRouteEnum.SUCCESS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails, hasHandledLeadRedirect]);

  // Track which success page variant is shown to customer
  const handleVariantShown = React.useCallback((variantId: string) => {
    formController.successPage(variantId).catch(() => void 0);
  }, [formController]);

  const handleApplicationSuccess = () => {
    // Store the first name from the contact response
    if (formController.getContactFirstName()) {
      storageController.setContactFirstName(
        formController.getContactFirstName()
      );
    }

    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationSuccess();
    navigateToRoute(FormRouteEnum.SUCCESS);
  };

  const handleScheduleAppointment = async (
    selectedTime: Date
  ): Promise<boolean> => {
    try {
      const result = await formController.scheduleAppointment(selectedTime);
      return result.success;
    } catch (err) {
      console.error('Failed to schedule appointment:', err);
      return false;
    }
  };

  const handleInitiateCall = async () => {
    try {
      const result = await formController.connectCall();
      return {
        success: result.success,
        from: result.from,
        error: result.error,
      };
    } catch (err) {
      console.error('Failed to connect call:', err);
      return {
        success: false,
        error: t('applicationFlow.errors.failedToConnectCall'),
      };
    }
  };

  const handleApplicationOffers = async (targetUrl: string) => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationOffers();

    setLoadingMessage(t('applicationFlow.loading.offers'));

    await delay();

    window.location.href = targetUrl;
  };

  const startOver = async (message: string) => {
    await formController.startOver();
    setSmsAttempts(0);
    prospectLock.reset();
    kbaLock.reset();
    setKbaQuestions([]);
    setFormData((prev) => ({ ...prev, smsCode: '' }));
    setError(message);
    navigateToRoute(FormRouteEnum.START);
  };

  type VerificationMethod = 'sms' | 'prospect' | 'kba';

  const exhaustMethod = (method: VerificationMethod) => {
    const setters: Record<VerificationMethod, () => void> = {
      sms: lockOutSms,
      prospect: prospectLock.lockOut,
      kba: kbaLock.lockOut,
    };
    setters[method]();
  };

  // Server-session outcomes (401/409 start over, 429 method exhausted) take
  // precedence over the per-flow handling; returns true when handled.
  const handleSessionOutcome = async (code: number | undefined, method: VerificationMethod): Promise<boolean> => {
    const outcome = classifySessionOutcome(code);
    if (outcome === 'start-over') {
      await startOver(t('applicationFlow.errors.sessionExpired'));
      return true;
    }
    if (outcome === 'exhausted') {
      exhaustMethod(method);
      return true;
    }
    return false;
  };

  const handleApplicationFailure = () => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'unsuccessful',
    });
    storageController.clearFormData();
    storageController.setApplicationFailed();
    navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
  };

  const handleRejection = async (flowType: FlowType) => {
    const debtAmount = formController.getDebtAmount();
    const isEligibleForAutoEchelon = debtAmount > AUTO_ECHELON_DEBT_THRESHOLD;

    if (isEligibleForAutoEchelon) {
      const echelonResult = await formController.initiateAutoEchelonFlow({
        onProgress: (stage) => setEchelonStage(stage),
      });
      setEchelonStage(null);

      if (echelonResult.success && echelonResult.redirectUrl) {
        const url = new URL(echelonResult.redirectUrl);
        url.searchParams.set('embed', '1');
        window.location.href = url.toString();
        return;
      }
    }

    if (flowType === 'R1') {
      navigateToRoute(FormRouteEnum.VERIFY_SSN);
    } else if (flowType === 'N1') {
      setIsUnqualified(true);
      navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
    } else {
      setContactResult('rejected');
      navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formattedPhone }));
      setValidationErrors((prev) => ({
        ...prev,
        phone: validatePhone(formattedPhone),
      }));
    } else if (name === 'email') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setValidationErrors((prev) => ({
        ...prev,
        email: validateEmail(value),
      }));
    } else if (name === 'smsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else if (name === 'promoSmsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);
  };

  const handleConnectBySms = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);

    setValidationErrors({
      email: emailError,
      phone: phoneError,
    });

    if (emailError || phoneError) {
      return;
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage(t('applicationFlow.loading.secureConnection'));
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount,
        formData.loanPurpose,
        formData.employmentStatus,
        formData.employmentFrequency,
        formData.educationLevel,
        formData.annualIncome,
        formData.propertyStatus,
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        setSmsNeedsNewCode(false);
        storageController.setApplicationSmsCode();
        navigateToRoute(FormRouteEnum.VERIFY_PHONE);
      } else if (classifySessionOutcome(result.code) === 'exhausted') {
        startConnectCooldown();
      } else if (await handleSessionOutcome(result.code, 'sms')) {
        return;
      } else if (result.code === 500) {
        navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
      } else {
        throw new Error(result.error || t('applicationFlow.errors.failedToCreateConnection'));
      }
    } catch (err) {
      countSmsFailure();
      setError(err instanceof Error ? err.message : t('applicationFlow.errors.genericError'));
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleCodeVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoadingMessage(t('applicationFlow.loading.verifyingSmsCode'));
    setError(null);

    try {
      const verifyResult = await formController.verifyCode(
        formData.smsCode || '',
        formData
      );

      if (!verifyResult.success) {
        if (await handleSessionOutcome(verifyResult.code, 'sms')) {
          return;
        }
        if (verifyResult.code === 500) {
          navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
          return;
        }
        throw new Error(verifyResult.error);
      }

      setLoadingMessage(t('applicationFlow.loading.checkingEligibility'));
      const createResult = await formController.createContact(formData);

      if (!createResult.success) {
        if (await handleSessionOutcome(createResult.code, 'sms')) {
          return;
        }
        if (createResult.status === 'ERROR' && shouldFallback(createResult.code)) {
          setManualVerificationReason('create_failure');
          navigateToRoute(FormRouteEnum.MANUAL_VERIFY);
          return;
        }

        throw new Error(createResult.error);
      }

      switch (createResult.status) {
        case 'accepted':
        case 'accepted_tier2':
          if (flow === 'M1') {
            setContactResult('accepted');
            navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
          } else {
            handleApplicationSuccess();
          }
          break;

        case 'rejected':
          await handleRejection(flow);
          break;

        case 'ERROR':
          throw new Error(createResult.error || t('applicationFlow.errors.identityVerificationFailed'));
      }
    } catch (err) {
      countSmsFailure();
      setError(err instanceof Error ? err.message : t('applicationFlow.errors.genericError'));
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleResendCode = async () => {
    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage(t('applicationFlow.loading.sendingNewCode'));
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount,
        formData.loanPurpose,
        formData.employmentStatus,
        formData.employmentFrequency,
        formData.educationLevel,
        formData.annualIncome,
        formData.propertyStatus,
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        setSmsNeedsNewCode(false);
        setFormData((prev) => ({ ...prev, smsCode: '' }));
      } else if (await handleSessionOutcome(result.code, 'sms')) {
        return;
      } else {
        throw new Error(result.error || t('applicationFlow.errors.failedToSendNewCode'));
      }
    } catch (err) {
      countSmsFailure();
      setError(err instanceof Error ? err.message : t('applicationFlow.errors.genericError'));
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReenterInfo = () => {
    setFormData((prev) => ({ ...prev, smsCode: '' }));
    navigateToRoute(FormRouteEnum.BIRTH_DATE);
  };

  const handleUpdatePersonalInfo = () => {
    navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
  };

  const handleUpdateBirthDate = () => {
    navigateToRoute(FormRouteEnum.BIRTH_DATE);
  };

  const handleEditAllReviewDetails = () => {
    navigateToRoute(reviewEditController.startAll());
  };

  const handleResetAttempts = () => {
    setSmsAttempts(0);
    prospectLock.reset();
    kbaLock.reset();
    setError(null);
  };

  const handleChooseSms = () => {
    setError(null);
    navigateToRoute(FormRouteEnum.CONTACT_INFO);
  };

  const handleChooseKba = () => {
    setError(null);
    navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
  };

  const handleChooseProspect = () => {
    setError(null);
    setManualVerificationReason('sms_failure');
    navigateToRoute(FormRouteEnum.MANUAL_VERIFY);
  };

  // During a connect countdown there may be no code to enter; the contact step shows the wait.
  const handleManualVerificationBack = () => {
    navigateToRoute(connectCooldownUntil !== null ? FormRouteEnum.CONTACT_INFO : FormRouteEnum.VERIFY_PHONE);
  };

  const verifyWithExperianAndFallback = async (
    data: Partial<FormData>
  ): Promise<{ success: boolean; status?: string }> => {
    const experianResult = await formController.fallbackToExperian(data);

    if (experianResult.success && experianResult.qualified) {
      handleApplicationSuccess();
      return { success: true, status: 'accepted' };
    }

    if (!experianResult.success && shouldFallback(experianResult.code)) {
      const manualResult = await formController.createManualLead(data);

      if (manualResult.success && manualResult.qualified) {
        handleApplicationSuccess();
        return { success: true, status: 'accepted' };
      }

      if (!manualResult.success && manualResult.code === 400) {
        throw new Error(t('applicationFlow.errors.verificationFailedCheckDetails'));
      }

      handleApplicationFailure();
      return { success: true, status: 'rejected' };
    }

    handleApplicationFailure();
    return { success: true, status: 'rejected' };
  };

  const handleManualVerification = async (
    data: Partial<FormData>
  ): Promise<{ success: boolean; status?: string; error?: string }> => {
    setLoading(true);
    setLoadingMessage(t('applicationFlow.loading.verifyingInformation'));
    setError(null);

    const mergedData = { ...formData, ...data };
    setFormData(mergedData);

    try {
      if (manualVerificationReason === 'sms_failure') {
        const prospectResult = await formController.prospectVerify(mergedData);

        if (!prospectResult.success) {
          if (await handleSessionOutcome(prospectResult.code, 'prospect')) {
            return { success: false };
          }
          prospectLock.countFailure();
          const errorMsg = t('applicationFlow.errors.unableToVerifyIdentity');
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }
      }

      return await verifyWithExperianAndFallback(mergedData);
    } catch (err) {
      prospectLock.countFailure();
      const errorMsg = err instanceof Error ? err.message : t('applicationFlow.errors.genericError');
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleKbaPersonalInfoSubmit = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    navigateToRoute(FormRouteEnum.KBA_SSN);
  };

  const handleKbaSsnSubmit = async (data: Partial<FormData>) => {
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);
    setLoading(true);
    setLoadingMessage(t('applicationFlow.loading.verifyingIdentity'));
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectKba(
        updatedFormData,
        ipAddress,
        marketingParams.getParams()
      );

      if (!result.success) {
        if (await handleSessionOutcome(result.code, 'kba')) {
          return;
        }
        throw new Error(result.error || t('applicationFlow.errors.failedToStartIdentityVerification'));
      }

      if (!result.questions || result.questions.length === 0) {
        throw new Error(t('applicationFlow.errors.noSecurityQuestions'));
      }

      setKbaAttemptCount(1);
      
      setKbaQuestions(result.questions);
      navigateToRoute(FormRouteEnum.KBA_QUESTIONS);
    } catch (err) {
      kbaLock.countFailure();
      setError(err instanceof Error ? err.message : t('applicationFlow.errors.genericError'));
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleKbaQuestionsSubmit = async (answers: Record<string, string>) => {
    setLoading(true);
    setLoadingMessage(t('applicationFlow.loading.submittingAnswers'));
    setError(null);

    try {
      const verifyResult = await formController.verifyKba(answers, formData.email);

      if (!verifyResult.success) {
        if (await handleSessionOutcome(verifyResult.code, 'kba')) {
          return;
        }
        throw new Error(verifyResult.error || t('applicationFlow.errors.identityVerificationFailed'));
      }

      if (
        verifyResult.connectionStatus === 'IN_PROGRESS' &&
        verifyResult.questions &&
        verifyResult.questions.length > 0
      ) {
        kbaLock.countFailure();

        if (isKbaExhausted(kbaAttempts + 1)) {
          setError(t('applicationFlow.errors.unableToVerifyIdentity'));
          return;
        }

        const nextAttempt = kbaAttemptCount + 1;
        if (nextAttempt > MAX_KBA_ATTEMPTS) {
          setError(t('applicationFlow.errors.unableToVerifyIdentity'));
          return;
        }

        setKbaAttemptCount(nextAttempt);
        setKbaQuestions(verifyResult.questions);
        return;
      }

      setLoadingMessage(t('applicationFlow.loading.checkingEligibility'));
      const createResult = await formController.createContact(formData);

      if (!createResult.success) {
        if (await handleSessionOutcome(createResult.code, 'kba')) {
          return;
        }
        if (createResult.status === 'ERROR' && shouldFallback(createResult.code)) {
          await verifyWithExperianAndFallback(formData);
          return;
        }
        throw new Error(createResult.error);
      }

      switch (createResult.status) {
        case 'accepted':
        case 'accepted_tier2':
          handleApplicationSuccess();
          break;
        case 'rejected':
          await handleRejection(flow);
          break;
        default:
          navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
      }
    } catch (err) {
      kbaLock.countFailure();
      setError(err instanceof Error ? err.message : t('applicationFlow.errors.genericError'));
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleFormStep = (data: Partial<FormData>) => {
    if (currentStep === 0) {
      storageController.setApplicationStarted();
    }

    setFormData((prev) => {
      
      const updatedData = { ...prev, ...data };
      
      // Fix: Use UPDATED data, not stale formData
      marketingParams.updateFormData(updatedData);
      
      return updatedData;
    });

    if (reviewEditController.isActive()) {
      formDefaultsService.markStepSkipped(currentPath);
      navigateToRoute(reviewEditController.advance());
      return;
    }

    if (data.employmentStatus) {
      if (SKIP_FREQUENCY_STATUSES.includes(data.employmentStatus)) {
        setFormData((prev) => ({ ...prev, employmentFrequency: 'monthly' }));
        navigateToRoute(FormRouteEnum.ANNUAL_INCOME);
        return;
      }
    }

    const nextRoute = routeManager.getNextRoute(currentStep);

    if (flow === 'M1' && nextRoute === FormRouteEnum.VERIFY_SSN) {
      if (contactResult === 'accepted') {
        handleApplicationSuccess();
      } else {
        navigateToRoute(FormRouteEnum.VERIFY_SSN);
      }
      return;
    }

    if (nextRoute) {
      navigateToRoute(nextRoute);
    }
  };

  const submitOffers = async (email: string) => {
    setLoading(true);
    setLoadingMessage(t('applicationFlow.loading.submittingApplication'));
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.submitApplication({ ...formData, email }, ipAddress);
      const targetUrl = result.success
        ? resolveOffersRedirect({
            redirectUrl: result.redirectUrl,
            storeUuid: result.storeUuid,
            offerId: result.offerId,
            offersUrl: clientConfig.offersUrl,
          })
        : null;

      if (targetUrl) {
        await handleApplicationOffers(targetUrl);
      } else {
        handleApplicationFailure();
      }
    } catch (err) {
      handleApplicationFailure();
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleOffersSubmit = async () => {
    if (!formData.email) {
      setShowEmailPrompt(true);
      return;
    }
    await submitOffers(formData.email);
  };

  const handleEmailVerified = async (email: string) => {
    setFormData(prev => ({ ...prev, email }));
    setShowEmailPrompt(false);
    await submitOffers(email);
  };

  const handleBack = () => {
    setError(null);
    const prevRoute = routeManager.getPreviousRoute(currentStep);
    if (prevRoute) {
      navigateToRoute(prevRoute);
    }
  };

  const isRedirectingToAcceptedLead = Boolean(
    leadDetails &&
    isAcceptedApplicationStatus(leadDetails.applicationStatus) &&
    !hasHandledLeadRedirect &&
    currentPath !== FormRouteEnum.SUCCESS
  );

  const smsExhausted = isSmsExhausted(smsAttempts);
  const prospectExhausted = isProspectExhausted(prospectAttempts);
  const kbaExhausted = isKbaExhausted(kbaAttempts);

  const smsAlternatives: AlternativeMethodCallbacks = {
    onChooseKba: !kbaExhausted ? handleChooseKba : undefined,
    onChooseProspect: !prospectExhausted ? handleChooseProspect : undefined,
  };

  const kbaAlternatives: AlternativeMethodCallbacks = {
    onChooseSms: !smsExhausted ? handleChooseSms : undefined,
    onChooseProspect: !prospectExhausted ? handleChooseProspect : undefined,
  };

  const prospectAlternatives: AlternativeMethodCallbacks = {
    onChooseSms: !smsExhausted ? handleChooseSms : undefined,
    onChooseKba: !kbaExhausted ? handleChooseKba : undefined,
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {echelonStage && <EchelonProgressOverlay activeStage={echelonStage} />}
      {loading && !echelonStage && <LoadingOverlay message={loadingMessage} />}
      {isPartnerLoading && (
        <LoadingOverlay message={t('applicationFlow.loading.personalizedDetails')} />
      )}
      {(isLeadPreloadLoading || isRedirectingToAcceptedLead || (isTerminalRoute && terminalAccess === 'checking')) && (
        <LoadingOverlay message={t('applicationFlow.loading.application')} />
      )}

      {showEmailPrompt && (
        <EmailVerificationModal
          onConfirm={handleEmailVerified}
          loading={loading}
        />
      )}

      <ProgressBar
        currentStep={displayProgressStep}
        totalSteps={totalSteps}
        onBack={!ROUTES_WITHOUT_BACK.has(currentPath) ? handleBack : undefined}
      />

      <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
        <FormHeader />

        <div className="p-6">
          {currentPath === FormRouteEnum.START && (
            showPartnerReview ? (
              <ApplicationReviewForm
                formData={formData}
                onContinue={() => handleFormStep({})}
                onEditAllDetails={handleEditAllReviewDetails}
              />
            ) : (
              <StartForm onSubmit={handleFormStep} />
            )
          )}

          {currentPath === FormRouteEnum.EMAIL && (
            <EmailForm
              formData={formData}
              onChange={handleChange}
              onSuggestionAccept={(email) => {
                setFormData((prev) => ({ ...prev, email }));
                setValidationErrors((prev) => ({
                  ...prev,
                  email: validateEmail(email),
                }));
              }}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.BIRTH_DATE && (
            <BirthDateForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.CONTACT_INFO && (
            <ContactInfoForm
              formData={formData}
              validationErrors={validationErrors}
              loading={loading}
              error={error}
              cooldownSecondsLeft={connectCooldownSecondsLeft}
              onChange={handleChange}
              onSubmit={handleConnectBySms}
              {...smsAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.VERIFY_PHONE && (
            <PhoneVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleCodeVerification}
              onResendCode={handleResendCode}
              onReenterInfo={handleReenterInfo}
              smsAttempts={smsAttempts}
              methodExhausted={smsExhausted}
              cooldownSecondsLeft={connectCooldownSecondsLeft}
              needsNewCode={smsNeedsNewCode}
              {...smsAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.LOAN_AMOUNT && (
            <LoanAmountForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.LOAN_PURPOSE && (
            <LoanPurposeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.PROPERTY_STATUS && (
            <PropertyStatusForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.EMPLOYMENT_STATUS && (
            <EmploymentStatusForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.EMPLOYMENT_FREQUENCY && (
            <EmploymentFrequencyForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.ANNUAL_INCOME && (
            <AnnualIncomeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.EDUCATION && (
            <EducationForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.VERIFY_SSN && (
            <SSNVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleOffersSubmit}
            />
          )}

          {currentPath === FormRouteEnum.SUCCESS && terminalAccess === 'full' && (
            <SuccessStep
              contactCenterAvailability={contactCenterAvailability}
              shouldSendCall={shouldSendCall}
              onScheduleAppointment={handleScheduleAppointment}
              onInitiateCall={handleInitiateCall}
              isBpoAgent={isBpoAgent}
              onVariantShown={handleVariantShown}
            />
          )}

          {currentPath === FormRouteEnum.UNSUCCESSFUL && (
            <UnsuccessfulApplication />
          )}

          {currentPath === FormRouteEnum.BLOCK && (terminalAccess === 'full' || terminalAccess === 'local-only') && (
            <ApplicationBlocked onInitiateCall={handleInitiateCall} onStartNew={handleResetAttempts} />
          )}

          {currentPath === FormRouteEnum.MANUAL_VERIFY && (
            <ManualVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleManualVerification}
              onBack={handleManualVerificationBack}
              onChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
              showOfferCode={manualVerificationReason === 'sms_failure'}
              methodExhausted={prospectExhausted}
              cooldownSecondsLeft={prospectLock.lockSecondsLeft}
              smsExhausted={smsExhausted}
              {...prospectAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.KBA_PERSONAL_INFO && (
            <KbaPersonalInfoForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleKbaPersonalInfoSubmit}
              methodExhausted={kbaExhausted}
              cooldownSecondsLeft={kbaLock.lockSecondsLeft}
              {...kbaAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.KBA_SSN && (
            <KbaSsnForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleKbaSsnSubmit}
              methodExhausted={kbaExhausted}
              cooldownSecondsLeft={kbaLock.lockSecondsLeft}
              onUpdatePersonalInfo={handleUpdatePersonalInfo}
              onUpdateBirthDate={handleUpdateBirthDate}
              {...kbaAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.KBA_QUESTIONS && (
            <KbaQuestionsForm
              questions={kbaQuestions}
              loading={loading}
              error={error}
              onSubmit={handleKbaQuestionsSubmit}
              methodExhausted={kbaExhausted}
              cooldownSecondsLeft={kbaLock.lockSecondsLeft}
              {...kbaAlternatives}
            />
          )}
        </div>
      </div>

      {currentPath === FormRouteEnum.START && (
        <>
          <div className="mt-8 text-center">
            <TrustpilotWidget size="large" />
          </div>
        </>
      )}

      <SecurityTiles />

      <DeveloperTools formData={formData} />
    </div>
  );
}